/**
 * Printing Toolkit filter helpers for OrderDesk folder-scoped orders.
 * Shipping / company dropdowns are populated from the loaded folder set.
 * Delivery / Dispatch day helpers support table columns and print-count day.
 */

import { formatYYYYMMDD } from '../services/dateService.js';
import { getOrderDeliveryDate, getOrderDispatchDate, normalizeOrderDate } from './orderDateUtils.js';

export function getPrintingOrderShippingMethod(order) {
  return String(
    order?.shipping_method ||
      order?.shippingMethod ||
      order?.shipping?.method ||
      order?.['Shipping Method'] ||
      '',
  ).trim();
}

export function getPrintingOrderCompany(order) {
  return String(
    order?.companyName ||
      order?.customerCompany ||
      order?.customer?.company ||
      order?.shipping?.company ||
      '',
  ).trim();
}

/** @returns {string|null} YYYY-MM-DD */
export function getPrintingOrderDeliveryDay(order) {
  const raw = getOrderDeliveryDate(order);
  if (!raw) return null;
  return normalizeOrderDate(String(raw)) || null;
}

/** @returns {string|null} YYYY-MM-DD */
export function getPrintingOrderDispatchDay(order) {
  const raw = getOrderDispatchDate(order);
  if (!raw) return null;
  return normalizeOrderDate(String(raw)) || null;
}

/**
 * Optional narrow after a membership folder load: keep only orders whose Delivery
 * or Dispatch equals the date-folder day. `all` keeps every OrderDesk folder member.
 * @param {object} order
 * @param {string} folderName DD-MM-YYYY or other
 * @param {'all'|'delivery'|'dispatch'} dateFilterField
 */
export function orderMatchesFolderDateScope(
  order,
  folderName,
  dateFilterField = 'all',
) {
  if (!dateFilterField || dateFilterField === 'all') return true;
  const folderIso = (() => {
    const name = String(folderName || '').trim();
    if (!/^\d{2}-\d{2}-\d{4}$/.test(name)) return '';
    return normalizeOrderDate(name) || '';
  })();
  if (!folderIso) return true;
  const day =
    dateFilterField === 'dispatch'
      ? getPrintingOrderDispatchDay(order)
      : getPrintingOrderDeliveryDay(order);
  return day === folderIso;
}

/** @deprecated use orderMatchesFolderDateScope */
export const orderMatchesPrintingFolderDateScope = orderMatchesFolderDateScope;

function formatShortDayDisplay(isoYmd) {
  if (!isoYmd) return '—';
  const d = new Date(`${isoYmd}T12:00:00`);
  if (Number.isNaN(d.getTime())) return isoYmd;
  return d.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });
}

/**
 * Unique shipping methods (+ Unspecified when blanks exist).
 * @returns {string[]}
 */
export function buildPrintingShippingMethodOptions(orders) {
  const methodSet = new Set();
  let hasBlank = false;
  (orders || []).forEach((o) => {
    const method = getPrintingOrderShippingMethod(o);
    if (method) methodSet.add(method);
    else hasBlank = true;
  });
  const methods = Array.from(methodSet).sort((a, b) => a.localeCompare(b));
  if (hasBlank) methods.push('Unspecified');
  return methods;
}

/**
 * Unique company names (+ Unspecified when blanks exist).
 * @returns {string[]}
 */
export function buildPrintingCompanyOptions(orders) {
  const companySet = new Set();
  let hasBlank = false;
  (orders || []).forEach((o) => {
    const company = getPrintingOrderCompany(o);
    if (company) companySet.add(company);
    else hasBlank = true;
  });
  const companies = Array.from(companySet).sort((a, b) => a.localeCompare(b));
  if (hasBlank) companies.push('Unspecified');
  return companies;
}

/**
 * Orders in scope for cascading shipping/company options.
 * Folder load already scopes the list; optional day filter retained for callers.
 */
export function filterOrdersForPrintingOptionScope(orders, filterDate) {
  if (!filterDate) return orders || [];
  return (orders || []).filter((o) => getPrintingOrderDeliveryDay(o) === filterDate);
}

/**
 * Short Delivery column label (e.g. Fri 24 Jul).
 */
export function formatPrintingDeliveryDisplay(order) {
  return formatShortDayDisplay(getPrintingOrderDeliveryDay(order));
}

/**
 * Short Dispatch column label (e.g. Thu 23 Jul).
 */
export function formatPrintingDispatchDisplay(order) {
  return formatShortDayDisplay(getPrintingOrderDispatchDay(order));
}

/** @deprecated Prefer OrderDesk folder auto-select; kept for older tests/callers. */
export function buildPrintingDeliveryDateOptions(orders) {
  const set = new Set();
  (orders || []).forEach((o) => {
    const day = getPrintingOrderDeliveryDay(o);
    if (day) set.add(day);
  });
  return Array.from(set)
    .sort((a, b) => b.localeCompare(a))
    .map((value) => {
      const d = value ? new Date(`${value}T12:00:00`) : null;
      const label =
        d && !Number.isNaN(d.getTime())
          ? `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`
          : value;
      return { value, label };
    });
}

/** @deprecated Prefer findAutoSelectOrderDeskFolder. */
export function findAutoSelectPrintingDeliveryDate(dateOptions, now = new Date()) {
  const values = (dateOptions || [])
    .map((o) => (typeof o === 'string' ? o : o?.value))
    .filter(Boolean);
  if (!values.length) return '';

  const today = new Date(now);
  today.setHours(12, 0, 0, 0);
  const currentDay = today.getDay();
  const daysUntilFriday = currentDay <= 5 ? 5 - currentDay : 5 + (7 - currentDay);
  const nextFriday = new Date(today);
  nextFriday.setDate(today.getDate() + daysUntilFriday);
  const nextFriIso = formatYYYYMMDD(nextFriday);
  if (values.includes(nextFriIso)) return nextFriIso;

  const fridayValues = values.filter((iso) => {
    const d = new Date(`${iso}T12:00:00`);
    return !Number.isNaN(d.getTime()) && d.getDay() === 5;
  });
  if (fridayValues.length) {
    fridayValues.sort((a, b) => {
      const dA = new Date(`${a}T12:00:00`);
      const dB = new Date(`${b}T12:00:00`);
      return Math.abs(dA - today) - Math.abs(dB - today);
    });
    return fridayValues[0];
  }

  const sorted = [...values].sort((a, b) => {
    const dA = new Date(`${a}T12:00:00`);
    const dB = new Date(`${b}T12:00:00`);
    return Math.abs(dA - today) - Math.abs(dB - today);
  });
  return sorted[0] || '';
}
